import { Image, Paper, Stack, Text, Title } from "@mantine/core"
import { Logo } from "../../../components/Logo"
import type { IProps } from "./interfaces"

/**
 * Shared centered card for the public auth pages (invite / forgot / reset).
 * Pure presentation — mirrors the login page's background treatment so these
 * pages match it: Lexicanon (no custom bg) gets the full-bleed brand art
 * (`login-page--art`) + frosted-glass card; Voxcanon and companies with a custom
 * `loginBackground` keep their own full-bleed background.
 */
export const AuthShell = ({
  logo,
  appName,
  product = "lexicanon",
  loginBackground,
  title,
  subtitle,
  children,
}: IProps): React.JSX.Element => {
  const useArtBackground = product === "lexicanon" && !loginBackground
  return (
    <div
      className={`login-page${useArtBackground ? " login-page--art" : ""}`}
      style={loginBackground ? { backgroundImage: `url('${loginBackground}')` } : undefined}
    >
      <div className="login-content">
        <Paper
          radius="md"
          p="xl"
          withBorder
          bg={useArtBackground || product === "voxcanon" ? undefined : "var(--mantine-color-body)"}
          style={useArtBackground ? undefined : { opacity: 0.97 }}
        >
          <Stack align="center" mb="md">
            {logo ? (
              <Image src={logo} alt={appName ?? "Logo"} className="login-logo" fit="contain" />
            ) : (
              <Logo size={40} />
            )}
          </Stack>
          <Title order={3} ta="center" mb={subtitle ? 4 : "md"}>
            {title}
          </Title>
          {subtitle ? (
            <Text c="dimmed" size="sm" ta="center" mb="md">
              {subtitle}
            </Text>
          ) : null}
          {children}
        </Paper>
      </div>
    </div>
  )
}
